import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon, Download as DownloadIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormikErrors, useFormik } from 'formik';
import { useRef, useState } from 'react';
import * as yup from 'yup';

import { CountryAttachment, CountryLink, CountryPeopleListItem, CountryPetsListItem, typicodeApi } from 'shared/api';
import { ConfirmDialog } from 'shared/ui';

import {
  COUNTRY_ATTACHMENT_ACCEPT,
  formatCheckedDate,
  formatFileSize,
  validateCountryAttachment,
} from '../../lib/country-attachments';
import type { SaveCountryInfoArgs } from '../../model/country';

type CountryListItem = CountryPeopleListItem | CountryPetsListItem;

/**
 * Аргументы сохранения — совпадают с SaveCountryInfoArgs из модели один в один,
 * реэкспорт нужен только чтобы диалог не импортировал scope-специфичные вещи модели.
 */
export type CountryInfoSubmitArgs = SaveCountryInfoArgs;

type Props = {
  country: CountryListItem;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (args: CountryInfoSubmitArgs) => void;
};

// Ссылка формы со стабильным ключом для React — на бек он не уходит,
// вырезается перед сабмитом (см. onSubmit ниже)
type FormLink = CountryLink & { key: string };

type FormValues = {
  description: string;
  links: FormLink[];
};

// Ограничения повторяют валидацию бека один в один
const validationSchema = yup.object({
  description: yup.string().max(20000, 'Maximum 20000 characters'),
  links: yup
    .array()
    .max(50, 'Maximum 50 links')
    .of(
      yup.object({
        url: yup
          .string()
          .required('URL is required')
          .max(2000, 'Maximum 2000 characters')
          .matches(/^https?:\/\/.+/i, 'URL must start with http:// or https://'),
        title: yup.string().required('Title is required').max(200, 'Maximum 200 characters'),
      }),
    ),
});

export const CountryInfoDialog = ({ country, isSaving, onClose, onSubmit }: Props) => {
  // Текущее вложение на сервере с точки зрения диалога. Отдельно от country.info,
  // потому что после частичного успеха (POST/DELETE прошёл, а PATCH — нет) диалог
  // не размонтируется и не получает новый country — это состояние обновляют
  // колбэки onUploaded/onDeleted, чтобы повторный Save был идемпотентным.
  const [existingAttachment, setExistingAttachment] = useState<CountryAttachment | null>(
    country.info?.attachments?.[0] ?? null,
  );
  const [attachmentIdToRemove, setAttachmentIdToRemove] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Счётчик для генерации стабильных React-ключей строк ссылок — не связан с индексом
  // в массиве, поэтому переживает добавление/удаление строк из середины без сдвига ключей
  const linkKeyCounterRef = useRef(0);
  const generateLinkKey = () => `link-${linkKeyCounterRef.current++}`;

  // Какая из двух кнопок сабмита была нажата — Save или Confirm calendar.
  // Formik's onSubmit получает только values, поэтому режим передаём через ref,
  // выставляя его непосредственно перед вызовом handleSubmit
  const submitModeRef = useRef<'save' | 'confirm'>('save');

  // attachmentIdToRemove относится к КОНКРЕТНОМУ вложению, которое пометили на удаление,
  // а не к «текущему вложению» вообще. Если между пометкой на удаление и Save существующее
  // вложение успело замениться (POST прошёл, onUploaded обновил existingAttachment на новый
  // файл, а DELETE ещё не отработал — attachmentIdToRemove хранит id СТАРОГО файла), новое
  // вложение должно оставаться видимым, а не скрываться под id уже неактуальной пометки.
  const visibleAttachment =
    existingAttachment && existingAttachment.id === attachmentIdToRemove ? null : existingAttachment;
  const canUpload = !visibleAttachment && !pendingFile;

  const formik = useFormik<FormValues>({
    initialValues: {
      description: country.info?.description ?? '',
      links: (country.info?.links ?? []).map((link) => ({ ...link, key: generateLinkKey() })),
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit({
        code: country.countryCode,
        dto: {
          description: values.description,
          links: values.links.map((link) => ({ url: link.url, title: link.title })),
          updateLastChecked: submitModeRef.current === 'confirm' ? true : undefined,
        },
        fileToUpload: pendingFile,
        attachmentIdToRemove,
        onUploaded: (attachment) => {
          setPendingFile(null);
          setExistingAttachment(attachment);
        },
        onDeleted: () => {
          setExistingAttachment(null);
          setAttachmentIdToRemove(null);
        },
      });
    },
  });

  const linkErrors = (formik.errors.links ?? []) as Array<FormikErrors<FormLink>>;
  const linkTouched = formik.touched.links ?? [];

  // Есть несохранённые изменения — форма «грязная», или выбран/помечен на удаление файл
  const hasUnsavedChanges = formik.dirty || pendingFile !== null || attachmentIdToRemove !== null;

  /**
   * Актуальный список ссылок, обновляемый синхронно при каждом клике.
   *
   * Функционального апдейта Formik здесь мало: внутри одного React-батча несколько
   * кликов резолвят колбэк от одного и того же состояния, и добавленные строки
   * затирают друг друга (проверено — три быстрых клика давали одну строку).
   * Ref обновляется до перерисовки, поэтому клики накапливаются корректно.
   */
  const linksRef = useRef(formik.values.links);

  // Синхронизируем ref со значением формы: оно меняется и помимо кликов (сброс, ре-инициализация)
  linksRef.current = formik.values.links;

  const commitLinks = (links: FormLink[]) => {
    linksRef.current = links;
    void formik.setFieldValue('links', links);
  };

  const handleAddLink = () => {
    commitLinks([...linksRef.current, { key: generateLinkKey(), url: '', title: '' }]);
  };

  const handleRemoveLink = (key: string) => {
    commitLinks(linksRef.current.filter((link) => link.key !== key));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Сбрасываем value, иначе повторный выбор того же файла не вызовет change
    event.target.value = '';

    if (!file) return;

    const error = validateCountryAttachment(file);

    if (error) {
      setFileError(error);
      return;
    }

    setFileError(null);
    setPendingFile(file);
  };

  const handleDownload = async () => {
    if (!visibleAttachment) return;

    setIsDownloading(true);

    try {
      const response = await typicodeApi.files.getFileByPath(visibleAttachment.path);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');

      link.href = url;
      link.download = visibleAttachment.name;
      // Firefox/Safari не запускают загрузку, если якорь не в DOM на момент click()
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Освобождаем URL асинхронно — синхронный revoke сразу после click()
      // может сорвать загрузку, которая ещё не успела прочитать blob
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
      setFileError('Failed to download the file');
    } finally {
      setIsDownloading(false);
    }
  };

  // Переводит фокус (и прокрутку) на первое невалидное поле, чтобы Save
  // при невалидной ссылке за пределами видимой области диалога не выглядел «сломанным»
  const focusFirstInvalidField = (errors: FormikErrors<FormValues>) => {
    let selector: string | null = null;

    if (errors.description) {
      selector = '[name="description"]';
    } else if (errors.links) {
      const errorsLinks = errors.links as unknown as Array<FormikErrors<FormLink>> | string;
      if (Array.isArray(errorsLinks)) {
        const index = errorsLinks.findIndex(
          (linkError) => linkError && (Boolean(linkError.url) || Boolean(linkError.title)),
        );
        if (index !== -1) {
          const field = errorsLinks[index]?.url ? 'url' : 'title';
          selector = `[name="links[${index}].${field}"]`;
        }
      }
    }

    if (!selector) return;

    const field = document.querySelector<HTMLElement>(selector);
    field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    field?.focus();
  };

  // Общая логика для Save и Confirm calendar — отличаются только режимом,
  // который решает, уйдёт ли updateLastChecked в dto (см. onSubmit формы выше)
  const handleSubmitClick = async (mode: 'save' | 'confirm') => {
    submitModeRef.current = mode;
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      focusFirstInvalidField(errors);
    }
    // handleSubmit сам протрогает все поля и покажет ошибки; если форма невалидна,
    // onSubmit не вызовется
    void formik.handleSubmit();
  };

  const requestClose = () => {
    if (isSaving) return;

    if (hasUnsavedChanges) {
      setConfirmCloseOpen(true);
      return;
    }

    onClose();
  };

  const handleConfirmDiscard = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleDialogClose = (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
    // Случайный клик по подложке не должен выбрасывать несохранённую форму (до 20000 символов в Notes)
    if (reason === 'backdropClick') return;
    requestClose();
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={handleDialogClose}>
      <DialogTitle>
        National calendar — {country.countryName}
        <Typography variant="body2" color="text.secondary">
          {country.info?.needsCheck
            ? 'This country needs a check'
            : `Last checked ${formatCheckedDate(country.info?.lastCheckedAt)}`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Notes"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={
              formik.touched.description && formik.errors.description
                ? formik.errors.description
                : 'Ministry of Health website, social media accounts, any free-form notes'
            }
          />

          <Divider />

          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1">Source links</Typography>
              <Button size="small" disabled={formik.values.links.length >= 50} onClick={handleAddLink}>
                Add link
              </Button>
            </Box>

            {formik.values.links.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No links yet
              </Typography>
            )}

            <Stack spacing={1}>
              {formik.values.links.map((link, index) => (
                <Box key={link.key} display="flex" alignItems="flex-start" gap={1}>
                  <TextField
                    required
                    sx={{ flex: 2 }}
                    size="small"
                    label="URL"
                    placeholder="https://example.org/calendar"
                    name={`links[${index}].url`}
                    value={link.url}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={Boolean(linkTouched[index]?.url) && Boolean(linkErrors[index]?.url)}
                    helperText={linkTouched[index]?.url && linkErrors[index]?.url}
                  />
                  <TextField
                    required
                    sx={{ flex: 1 }}
                    size="small"
                    label="Title"
                    name={`links[${index}].title`}
                    value={link.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={Boolean(linkTouched[index]?.title) && Boolean(linkErrors[index]?.title)}
                    helperText={linkTouched[index]?.title && linkErrors[index]?.title}
                  />
                  <IconButton aria-label="remove link" color="error" onClick={() => handleRemoveLink(link.key)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" mb={1}>
              National calendar file
            </Typography>

            {visibleAttachment && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">
                  {visibleAttachment.name} ({formatFileSize(visibleAttachment.size)})
                </Typography>
                <Tooltip title="Download">
                  <span>
                    <IconButton aria-label="download attachment" disabled={isDownloading} onClick={handleDownload}>
                      <DownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove — will be applied on save">
                  <IconButton
                    aria-label="remove attachment"
                    color="error"
                    onClick={() => setAttachmentIdToRemove(existingAttachment?.id ?? null)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {pendingFile && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">
                  {pendingFile.name} ({formatFileSize(pendingFile.size)}) — will be uploaded on save
                </Typography>
                <IconButton aria-label="cancel selected file" onClick={() => setPendingFile(null)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}

            {!visibleAttachment && !pendingFile && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                No file attached
              </Typography>
            )}

            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept={COUNTRY_ATTACHMENT_ACCEPT}
              onChange={handleFileChange}
            />
            <Button
              startIcon={<CloudUploadIcon />}
              disabled={!canUpload}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mt: 1 }}
            >
              Upload file
            </Button>
            <Typography variant="caption" color="text.secondary" display="block">
              Allowed files: PDF, DOC, XLS, images and archives, up to 10 MB
            </Typography>
            {!canUpload && (
              <Typography variant="caption" color="text.secondary" display="block">
                Only one attachment is allowed — remove the current one first
              </Typography>
            )}

            {fileError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {fileError}
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>

      {/*
        Порядок: Cancel — Confirm calendar — Save. Заливка у Save: это действие по умолчанию,
        Confirm calendar рядом как вторая, явно выбираемая операция.

        disableSpacing обязателен: штатный отступ DialogActions задан селектором
        `& > :not(:first-of-type)`, а :first-of-type считается ПО ТИПУ элемента. Tooltip оборачивает
        кнопку в <span>, тот оказывается первым span-ом и отступ не получает — в итоге между
        кнопками выходит 8px и 16px. Отключаем встроенный spacing и разносим только через gap.
      */}
      <DialogActions disableSpacing sx={{ gap: 1, px: 3, py: 2 }}>
        <Button disabled={isSaving} onClick={requestClose}>
          Cancel
        </Button>
        <Tooltip title="Marks the calendar as checked and postpones the next reminder by 6 months">
          <span>
            <LoadingButton
              variant="outlined"
              loading={isSaving}
              onClick={() => {
                void handleSubmitClick('confirm');
              }}
            >
              Confirm calendar
            </LoadingButton>
          </span>
        </Tooltip>
        <LoadingButton
          variant="contained"
          loading={isSaving}
          onClick={() => {
            void handleSubmitClick('save');
          }}
        >
          Save
        </LoadingButton>
      </DialogActions>

      <ConfirmDialog
        open={confirmCloseOpen}
        title="Discard changes?"
        message="You have unsaved changes that will be lost. Close anyway?"
        confirmLabel="Discard"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setConfirmCloseOpen(false)}
      />
    </Dialog>
  );
};
